/**
 * LoginPage — WhatsApp OTP Authentication
 * Design: "Ethereal Command" — exact match to code.html / DESIGN.md
 *
 * FIXED VERSION:
 *  - Removed unused imports
 *  - Added error logging
 *  - Added proper cleanup for timeouts
 *  - Optimized style objects
 *  - Added loading state for country change
 *  - Added missing ARIA labels for accessibility
 */

import React, { useEffect, useRef, useState, useCallback } from "react";
import { isValidPhoneNumber } from "libphonenumber-js";
import toast from "react-hot-toast";

interface Props {
  onLogin: (user: { name: string; role: string; phone: string }) => void;
}

// Country list backed by libphonenumber-js country codes
const COUNTRIES = [
  { iso: "AE", code: "971", flag: "🇦🇪", name: "United Arab Emirates" },
  { iso: "SA", code: "966", flag: "🇸🇦", name: "Saudi Arabia" },
  { iso: "KW", code: "965", flag: "🇰🇼", name: "Kuwait" },
  { iso: "QA", code: "974", flag: "🇶🇦", name: "Qatar" },
  { iso: "BH", code: "973", flag: "🇧🇭", name: "Bahrain" },
  { iso: "OM", code: "968", flag: "🇴🇲", name: "Oman" },
  { iso: "GB", code: "44",  flag: "🇬🇧", name: "United Kingdom" },
  { iso: "US", code: "1",   flag: "🇺🇸", name: "United States / Canada" },
  { iso: "IN", code: "91",  flag: "🇮🇳", name: "India" },
  { iso: "PK", code: "92",  flag: "🇵🇰", name: "Pakistan" },
  { iso: "IR", code: "98",  flag: "🇮🇷", name: "Iran" },
  { iso: "TR", code: "90",  flag: "🇹🇷", name: "Turkey" },
  { iso: "EG", code: "20",  flag: "🇪🇬", name: "Egypt" },
  { iso: "JO", code: "962", flag: "🇯🇴", name: "Jordan" },
  { iso: "LB", code: "961", flag: "🇱🇧", name: "Lebanon" },
  { iso: "MA", code: "212", flag: "🇲🇦", name: "Morocco" },
  { iso: "DE", code: "49",  flag: "🇩🇪", name: "Germany" },
  { iso: "FR", code: "33",  flag: "🇫🇷", name: "France" },
  { iso: "CN", code: "86",  flag: "🇨🇳", name: "China" },
  { iso: "RU", code: "7",   flag: "🇷🇺", name: "Russia" },
];

type Step = "phone" | "otp";

const LoginPage: React.FC<Props> = ({ onLogin }) => {
  const [step, setStep]              = useState<Step>("phone");
  const [country, setCountry]        = useState(COUNTRIES[0]); // UAE default
  const [rawPhone, setRawPhone]      = useState("");
  const [phoneError, setPhoneError]  = useState("");
  const [otp, setOtp]                = useState(["", "", "", "", "", ""]);
  const [loading, setLoading]        = useState(false);
  const [error, setError]            = useState("");
  const [resendCd, setResendCd]      = useState(0);
  const [countryChanging, setCountryChanging] = useState(false);
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);
  const timeoutRef = useRef<NodeJS.Timeout>();

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (resendCd <= 0) return;
    timeoutRef.current = setTimeout(() => setResendCd(n => n - 1), 1000);
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [resendCd]);

  // ── Phone validation ──────────────────────────────────────────────────────
  const validatePhone = useCallback((digits: string): boolean => {
    if (!digits) { setPhoneError("Enter your phone number"); return false; }
    try {
      const full = `+${country.code}${digits}`;
      const valid = isValidPhoneNumber(full, country.iso as any);
      if (!valid) {
        setPhoneError(`Not a valid ${country.name} number`);
        return false;
      }
    } catch (error) {
      console.error("Phone validation error:", error);
      setPhoneError("Invalid phone number");
      return false;
    }
    setPhoneError("");
    return true;
  }, [country]);

  // ── Step 1: Request OTP ──────────────────────────────────────────────────
  const requestOtp = async () => {
    setError("");
    const cleaned = rawPhone.replace(/\D/g, "");
    if (!validatePhone(cleaned)) return;

    setLoading(true);
    try {
      const res = await fetch("/api/auth/otp/request", {
        method: "POST",
        credentials: "include",                     // ← critical: store cookies
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ country_code: country.code, phone: cleaned }),
      });
      const data = await res.json();
      if (!res.ok) { 
        const errorMsg = data.error || data.detail || "Failed to send OTP";
        console.error("OTP request failed:", errorMsg, { phone: cleaned });
        setError(errorMsg);
        return; 
      }

      setStep("otp");
      setResendCd(60);
      setTimeout(() => otpRefs.current[0]?.focus(), 150);

      // Dev mode auto-fill
      if (data.dev_otp) {
        setOtp(String(data.dev_otp).split(""));
        toast("🔧 Dev mode: OTP auto-filled", { style: { background: "#171f33", color: "#c8bfff" } });
      } else {
        toast.success("Code sent to your WhatsApp");
      }
    } catch (error) {
      console.error("OTP request network error:", error);
      setError("Network error — check your connection.");
    } finally {
      setLoading(false);
    }
  };

  // ── Step 2: Verify OTP ───────────────────────────────────────────────────
  const verifyOtp = async () => {
    setError("");
    const code = otp.join("");
    if (code.length !== 6 || !/^\d{6}$/.test(code)) {
      setError("Enter all 6 digits");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch("/api/auth/otp/verify", {
        method: "POST",
        credentials: "include",                     // ← critical: receive + save cookies
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          country_code: country.code,
          phone: rawPhone.replace(/\D/g, ""),
          otp: code,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        const errorMsg = data.error || data.detail || "Verification failed";
        console.error("OTP verification failed:", errorMsg, { phone: rawPhone });
        setError(errorMsg);
        // Shake the OTP boxes
        setOtp(["", "", "", "", "", ""]);
        setTimeout(() => otpRefs.current[0]?.focus(), 50);
        return;
      }
      onLogin({ name: data.name, role: data.role, phone: data.phone });
    } catch (error) {
      console.error("OTP verification network error:", error);
      setError("Network error — check your connection.");
    } finally {
      setLoading(false);
    }
  };

  // ── OTP input handlers ────────────────────────────────────────────────────
  const handleOtpChange = (i: number, val: string) => {
    const digit = val.replace(/\D/g, "").slice(-1);
    const next = [...otp];
    next[i] = digit;
    setOtp(next);
    if (digit && i < 5) otpRefs.current[i + 1]?.focus();
  };

  const handleOtpKey = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !otp[i] && i > 0) otpRefs.current[i - 1]?.focus();
    if (e.key === "Enter") verifyOtp();
  };

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pasted.length === 6) {
      setOtp(pasted.split(""));
      otpRefs.current[5]?.focus();
    }
  };

  // ── UAE phone placeholder (starts with 5) ────────────────────────────────
  const getPlaceholder = () => {
    if (country.iso === "AE") return "5X XXX XXXX";
    if (country.iso === "SA") return "5X XXX XXXX";
    return "Local number";
  };

  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setCountryChanging(true);
    const c = COUNTRIES.find(x => x.iso === e.target.value) || COUNTRIES[0];
    setCountry(c);
    setRawPhone("");
    setPhoneError("");
    setTimeout(() => setCountryChanging(false), 100);
  };

  return (
    <div style={styles.page}>
      {/* Google Fonts + Material Symbols */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Manrope:wght@400;600;700;800&family=Space+Grotesk:wght@400;500;600;700&display=swap');
        @import url('https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:wght,FILL@100..700,0..1&display=swap');

        .material-symbols-outlined {
          font-variation-settings: 'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24;
          font-family: 'Material Symbols Outlined';
          font-style: normal;
          line-height: 1;
          letter-spacing: normal;
          text-transform: none;
          display: inline-block;
          white-space: nowrap;
          word-wrap: normal;
          direction: ltr;
          -webkit-font-smoothing: antialiased;
        }
        .ms-filled { font-variation-settings: 'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 24; }

        .otp-input {
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .otp-input:focus {
          outline: none;
          border-color: #c8bfff !important;
          box-shadow: 0 0 10px 1px rgba(200,191,255,0.4);
        }
        .phone-field:focus {
          outline: none;
          border-color: #c8bfff !important;
          box-shadow: 0 0 10px 1px rgba(200,191,255,0.25);
        }
        .btn-primary:hover { transform: scale(0.98); box-shadow: 0 0 20px rgba(255,214,165,0.35); }
        .btn-primary:active { transform: scale(0.96); }
        .btn-primary { transition: transform 0.15s, box-shadow 0.2s; }
        select option { background: #131b2e; color: #dae2fd; }
        .icon-btn:hover { color: #ffd6a5; }
        .icon-btn { transition: color 0.15s; }

        /* ── Mobile responsive ─────────────────────────────── */
        @media (max-width: 600px) {
          .login-header {
            padding: 16px 20px !important;
          }
          .login-brand {
            font-size: 17px !important;
          }
          .login-main {
            padding: 0 12px !important;
          }
          .login-card {
            padding: 32px 20px !important;
            border-radius: 10px !important;
          }
          .login-heading {
            font-size: 22px !important;
            letter-spacing: -0.02em !important;
          }
          .login-subtext {
            font-size: 12px !important;
          }
          .login-progress {
            margin-top: 28px !important;
          }
          .otp-input {
            font-size: 20px !important;
            border-radius: 6px !important;
          }
        }
        @media (max-width: 380px) {
          .login-card {
            padding: 24px 14px !important;
          }
          .login-heading {
            font-size: 19px !important;
          }
          .otp-input {
            font-size: 17px !important;
          }
        }
      `}</style>

      {/* Ethereal background blobs */}
      <div style={{ position: "absolute", top: "-10%", left: "-10%", width: "40%", height: "40%",
        background: "rgba(200,191,255,0.1)", borderRadius: "50%", filter: "blur(120px)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", bottom: "-5%", right: "-5%", width: "30%", height: "30%",
        background: "rgba(255,214,165,0.05)", borderRadius: "50%", filter: "blur(100px)", pointerEvents: "none" }} />

      {/* Brand logo watermark — centered behind login card */}
      <div style={{ position: "fixed", inset: 0, zIndex: 0, pointerEvents: "none", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
        <img
          src="/brand/afaq-logo.png"
          alt=""
          style={{ width: "55%", maxWidth: 480, opacity: 0.04, filter: "blur(1px) saturate(0)", objectFit: "contain" }}
        />
      </div>

      {/* Header */}
      <header style={styles.header}>
        <div style={styles.brand}>
          <img src="/brand/afaq-logo.png" alt="AFAQ" style={{ height: 36, width: "auto", objectFit: "contain", display: "block" }}
            onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
        </div>
        <div style={{ display: "flex", gap: 16 }}>
          <span className="material-symbols-outlined icon-btn" style={{ color: "#64748b", cursor: "pointer", fontSize: 22 }} aria-label="Help">help</span>
          <span className="material-symbols-outlined icon-btn" style={{ color: "#64748b", cursor: "pointer", fontSize: 22 }} aria-label="Language">language</span>
        </div>
      </header>

      {/* Main card */}
      <main className="login-main" style={{ width: "100%", maxWidth: 480, padding: "0 24px", position: "relative", zIndex: 10 }}>
        <div className="login-card" style={styles.card}>

          {/* Shield icon */}
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 32 }}>
            <div className="login-shield" style={styles.shieldWrap}>
              <span className="material-symbols-outlined ms-filled" style={{ fontSize: 32, color: "#432c08" }}>shield_person</span>
            </div>
          </div>

          {step === "phone" ? (
            <PhoneStep
              country={country}
              setCountry={handleCountryChange}
              rawPhone={rawPhone}
              setRawPhone={setRawPhone}
              phoneError={phoneError}
              setPhoneError={setPhoneError}
              onSubmit={requestOtp}
              loading={loading || countryChanging}
              error={error}
              placeholder={getPlaceholder()}
              countries={COUNTRIES}
            />
          ) : (
            <OtpStep
              phone={rawPhone}
              countryCode={country.code}
              otp={otp}
              otpRefs={otpRefs}
              onChange={handleOtpChange}
              onKeyDown={handleOtpKey}
              onPaste={handleOtpPaste}
              onVerify={verifyOtp}
              onBack={() => { setStep("phone"); setOtp(["","","","","",""]); setError(""); }}
              onResend={() => { requestOtp(); setOtp(["","","","","",""]); }}
              resendCd={resendCd}
              loading={loading}
              error={error}
            />
          )}
        </div>

        {/* Progress indicator — matches code.html exactly */}
        <div className="login-progress" style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 12, marginTop: 48 }}>
          <div style={{
            height: 4, width: 32, borderRadius: 99,
            background: step === "phone" ? "#ffd6a5" : "rgba(255,214,165,0.2)",
            boxShadow: step === "phone" ? "0 0 8px rgba(255,214,165,0.6)" : "none",
            transition: "all 0.3s",
          }} />
          <div style={{
            height: 6, width: 48, borderRadius: 99,
            background: step === "otp" ? "#ffd6a5" : "rgba(255,214,165,0.2)",
            boxShadow: step === "otp" ? "0 0 8px rgba(255,214,165,0.6)" : "none",
            transition: "all 0.3s",
          }} />
          <div style={{
            height: 4, width: 32, borderRadius: 99,
            background: "rgba(255,214,165,0.2)",
          }} />
        </div>
      </main>

      <footer style={styles.footer}>
        <p style={{ color: "#334155", fontFamily: "'Space Grotesk', sans-serif", fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase", margin: 0 }}>
          © 2025 AFAQ Lumina OS. Ethereal Command Protocol.
        </p>
      </footer>
    </div>
  );
};


// ── Phone Step ────────────────────────────────────────────────────────────────

const PhoneStep: React.FC<{
  country: typeof COUNTRIES[0];
  setCountry: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  rawPhone: string;
  setRawPhone: (v: string) => void;
  phoneError: string;
  setPhoneError: (v: string) => void;
  onSubmit: () => void;
  loading: boolean;
  error: string;
  placeholder: string;
  countries: typeof COUNTRIES;
}> = ({ country, setCountry, rawPhone, setRawPhone, phoneError, setPhoneError, onSubmit, loading, error, placeholder, countries }) => (
  <>
    <div style={{ textAlign: "center", marginBottom: 40 }}>
      <h1 className="login-heading" style={styles.heading}>Sign in to AFAQ OS</h1>
      <p className="login-subtext" style={styles.subtext}>Enter your registered WhatsApp number to receive a one-time verification code</p>
    </div>

    <div style={{ display: "flex", flexDirection: "column", gap: 20, marginBottom: 28 }}>
      {/* Country selector */}
      <div>
        <label style={styles.label} htmlFor="country-select">Country</label>
        <select
          id="country-select"
          value={country.iso}
          onChange={setCountry}
          style={styles.select}
          disabled={loading}
          aria-label="Select country"
        >
          {countries.map(c => (
            <option key={c.iso} value={c.iso}>
              {c.flag}  +{c.code} — {c.name}
            </option>
          ))}
        </select>
      </div>

      {/* Phone number */}
      <div>
        <label style={styles.label} htmlFor="phone-input">Phone Number</label>
        <div style={{ display: "flex" }}>
          <div style={styles.dialCode}>+{country.code}</div>
          <input
            id="phone-input"
            className="phone-field"
            type="tel"
            inputMode="numeric"
            placeholder={placeholder}
            value={rawPhone}
            onChange={e => {
              setRawPhone(e.target.value.replace(/\D/g, "").slice(0, 15));
              setPhoneError("");
            }}
            onKeyDown={e => e.key === "Enter" && onSubmit()}
            autoFocus
            style={styles.phoneInput}
            disabled={loading}
            aria-label="Phone number"
            aria-invalid={!!phoneError}
          />
        </div>
        {phoneError && (
          <p style={{ marginTop: 5, color: "#ffb4ab", fontFamily: "'Space Grotesk', sans-serif", fontSize: 11 }} role="alert">
            {phoneError}
          </p>
        )}
        {country.iso === "AE" && !phoneError && (
          <p style={{ marginTop: 5, color: "#4a5568", fontFamily: "'Space Grotesk', sans-serif", fontSize: 11 }}>
            UAE mobile: 9 digits starting with 5 (e.g. 581432494)
          </p>
        )}
      </div>
    </div>

    {error && <ErrorBanner message={error} />}

    <button 
      className="btn-primary" 
      onClick={onSubmit} 
      disabled={loading} 
      style={btnStyle(loading)}
      aria-label={loading ? "Sending code..." : "Send verification code"}
    >
      {loading ? "Sending..." : "Send Verification Code →"}
    </button>
  </>
);


// ── OTP Step ──────────────────────────────────────────────────────────────────

const OtpStep: React.FC<{
  phone: string;
  countryCode: string;
  otp: string[];
  otpRefs: React.MutableRefObject<(HTMLInputElement | null)[]>;
  onChange: (i: number, val: string) => void;
  onKeyDown: (i: number, e: React.KeyboardEvent<HTMLInputElement>) => void;
  onPaste: (e: React.ClipboardEvent) => void;
  onVerify: () => void;
  onBack: () => void;
  onResend: () => void;
  resendCd: number;
  loading: boolean;
  error: string;
}> = ({ phone, countryCode, otp, otpRefs, onChange, onKeyDown, onPaste, onVerify, onBack, onResend, resendCd, loading, error }) => {
  const allFilled = otp.join("").length === 6;
  return (
    <>
      <div style={{ textAlign: "center", marginBottom: 40 }}>
        <h1 style={styles.heading}>Enter verification code</h1>
        <p style={styles.subtext}>
          A 6-digit code has been sent to your WhatsApp.{" "}
          <br />
          <span style={{ color: "#ffd6a5", fontWeight: 600 }}>+{countryCode} {phone}</span>
        </p>
      </div>

      {/* 6-digit OTP inputs — matches code.html exactly */}
      <div 
        style={{ display: "flex", justifyContent: "space-between", gap: 8, marginBottom: 32 }} 
        onPaste={onPaste}
        role="group"
        aria-label="One-time password input"
      >
        {otp.map((digit, i) => (
          <input
            key={i}
            ref={el => { otpRefs.current[i] = el; }}
            className="otp-input"
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            placeholder="0"
            onChange={e => onChange(i, e.target.value)}
            onKeyDown={e => onKeyDown(i, e)}
            style={{
              flex: 1,
              aspectRatio: "1",
              textAlign: "center",
              fontSize: 24,
              fontWeight: 700,
              background: "#060e20",
              border: `2px solid ${digit ? "rgba(200,191,255,0.45)" : "rgba(78,69,59,0.2)"}`,
              borderRadius: 8,
              color: "#ffd6a5",
              fontFamily: "'Space Grotesk', sans-serif",
              boxShadow: "inset 0 2px 6px rgba(0,0,0,0.35)",
              cursor: "text",
              minWidth: 0,
            }}
            aria-label={`Digit ${i + 1}`}
            disabled={loading}
          />
        ))}
      </div>

      {error && <ErrorBanner message={error} />}

      {/* Primary CTA */}
      <button 
        className="btn-primary" 
        onClick={onVerify} 
        disabled={loading || !allFilled} 
        style={btnStyle(loading || !allFilled)}
        aria-label={loading ? "Verifying..." : "Verify and continue"}
      >
        {loading ? "Verifying..." : "Verify and Continue"}
      </button>

      {/* Secondary links — match code.html */}
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 16, marginTop: 28 }}>
        <button
          onClick={resendCd > 0 ? undefined : onResend}
          disabled={resendCd > 0 || loading}
          style={{
            background: "none", border: "none",
            color: resendCd > 0 ? "#334155" : "#c8bfff",
            cursor: resendCd > 0 ? "default" : "pointer",
            fontFamily: "'Space Grotesk', sans-serif",
            fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase",
            display: "flex", alignItems: "center", gap: 6,
            transition: "color 0.15s",
          }}
          aria-label={resendCd > 0 ? `Resend code in ${resendCd} seconds` : "Resend code"}
        >
          <span className="material-symbols-outlined" style={{ fontSize: 16 }}>refresh</span>
          {resendCd > 0 ? `Resend in ${resendCd}s` : "Resend code"}
        </button>
        <button
          onClick={onBack}
          disabled={loading}
          style={{
            background: "none", border: "none", cursor: "pointer",
            color: "#475569", fontFamily: "'Space Grotesk', sans-serif",
            fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase",
            display: "flex", alignItems: "center", gap: 6,
          }}
          aria-label="Change phone number"
        >
          <span className="material-symbols-outlined" style={{ fontSize: 16 }}>edit</span>
          Change phone number
        </button>
      </div>
    </>
  );
};


// ── Error banner ──────────────────────────────────────────────────────────────

const ErrorBanner: React.FC<{ message: string }> = ({ message }) => (
  <div 
    style={{
      background: "rgba(147,0,10,0.3)",
      border: "1px solid rgba(255,180,171,0.2)",
      borderRadius: 8, padding: "10px 16px", marginBottom: 16,
      color: "#ffb4ab", fontFamily: "'Space Grotesk', sans-serif",
      fontSize: 12, letterSpacing: "0.02em",
      display: "flex", alignItems: "center", gap: 8,
    }}
    role="alert"
    aria-live="polite"
  >
    <span className="material-symbols-outlined" style={{ fontSize: 16, flexShrink: 0 }}>error</span>
    {message}
  </div>
);


// ── Shared styles (cached to prevent recreation) ─────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: "100vh",
    background: "#0b1326",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "'Manrope', sans-serif",
    position: "relative",
    overflow: "hidden",
  },
  header: {
    position: "fixed", top: 0, width: "100%", zIndex: 50,
    display: "flex", justifyContent: "space-between", alignItems: "center",
    padding: "24px 40px",
    background: "transparent",
  },
  brand: {
    fontSize: 22, fontWeight: 800, letterSpacing: "-0.04em",
    color: "#ffd6a5", fontFamily: "'Manrope', sans-serif",
  },
  card: {
    background: "rgba(19,27,46,0.7)",
    backdropFilter: "blur(20px)",
    WebkitBackdropFilter: "blur(20px)",
    borderRadius: 12,
    padding: "48px 40px",
    boxShadow: "0px 24px 48px rgba(0,0,0,0.4)",
    border: "1px solid rgba(78,69,59,0.15)",
  },
  shieldWrap: {
    width: 64, height: 64, borderRadius: "50%",
    background: "linear-gradient(135deg, #ffd6a5, #e7c090)",
    display: "flex", alignItems: "center", justifyContent: "center",
    boxShadow: "0 8px 24px rgba(255,214,165,0.2)",
  },
  heading: {
    fontSize: 32, fontWeight: 800, letterSpacing: "-0.03em",
    color: "#dae2fd", marginBottom: 10, marginTop: 0,
    fontFamily: "'Manrope', sans-serif",
  },
  subtext: {
    color: "#9b8f83", fontFamily: "'Space Grotesk', sans-serif",
    fontSize: 13, letterSpacing: "0.02em", margin: 0,
  },
  label: {
    display: "block", marginBottom: 7,
    color: "#9b8f83", fontFamily: "'Space Grotesk', sans-serif",
    fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase",
  },
  select: {
    width: "100%", padding: "14px 16px",
    background: "#060e20", borderRadius: 8, border: "none",
    color: "#dae2fd", fontFamily: "'Space Grotesk', sans-serif",
    fontSize: 14, cursor: "pointer",
    boxShadow: "inset 0 2px 6px rgba(0,0,0,0.35)",
    appearance: "none" as any,
  },
  dialCode: {
    padding: "14px 16px", background: "#0d1626",
    borderRadius: "8px 0 0 8px",
    color: "#64748b", fontFamily: "'Space Grotesk', sans-serif", fontSize: 14,
    display: "flex", alignItems: "center",
    boxShadow: "inset 0 2px 6px rgba(0,0,0,0.35)",
    whiteSpace: "nowrap",
  },
  phoneInput: {
    flex: 1, padding: "14px 16px",
    background: "#060e20", borderRadius: "0 8px 8px 0",
    border: "2px solid transparent", color: "#dae2fd",
    fontFamily: "'Space Grotesk', sans-serif", fontSize: 18,
    letterSpacing: "0.06em",
    boxShadow: "inset 0 2px 6px rgba(0,0,0,0.35)",
  },
  footer: {
    position: "fixed", bottom: 0, width: "100%",
    display: "flex", justifyContent: "center", padding: "24px",
    background: "transparent",
  },
};

const btnStyle = (disabled: boolean): React.CSSProperties => ({
  width: "100%", padding: "16px",
  background: disabled
    ? "#2d3449"
    : "linear-gradient(135deg, #ffd6a5, #e7c090)",
  color: disabled ? "#64748b" : "#432c08",
  border: "none", borderRadius: 8,
  cursor: disabled ? "not-allowed" : "pointer",
  fontFamily: "'Space Grotesk', sans-serif",
  fontWeight: 700, fontSize: 13,
  letterSpacing: "0.15em", textTransform: "uppercase",
  boxShadow: disabled ? "none" : "0 8px 24px rgba(255,214,165,0.2)",
  marginTop: 8,
  display: "block",
});

export default LoginPage;